import type {
  StorefrontMediaAssetErrorCode,
  StorefrontMediaAssetOption,
} from '../../types/storefront-engine';
import {
  STOREFRONT_PUBLIC_MEDIA_ALLOWED_MIME_TYPES,
  STOREFRONT_PUBLIC_MEDIA_MAX_BYTES,
  STOREFRONT_PUBLIC_MEDIA_MAX_DIMENSION_PX,
  STOREFRONT_PUBLIC_MEDIA_MIN_DIMENSION_PX,
} from './public-media-reference';

export const STOREFRONT_MEDIA_UPLOAD_MAX_BYTES = STOREFRONT_PUBLIC_MEDIA_MAX_BYTES;
export const STOREFRONT_MEDIA_UPLOAD_MIN_DIMENSION_PX = STOREFRONT_PUBLIC_MEDIA_MIN_DIMENSION_PX;
export const STOREFRONT_MEDIA_UPLOAD_MAX_DIMENSION_PX = STOREFRONT_PUBLIC_MEDIA_MAX_DIMENSION_PX;
export const STOREFRONT_MEDIA_UPLOAD_ACCEPT = 'image/jpeg,image/png,image/webp';

export type StorefrontMediaUploadValidationError =
  | 'image_required'
  | 'image_too_large'
  | 'image_invalid_type'
  | 'image_dimensions_invalid';

export interface StorefrontMediaUploadInspection {
  readonly mimeType: 'image/jpeg' | 'image/png' | 'image/webp';
  readonly width: number;
  readonly height: number;
}

const JPEG_SOF_MARKERS = new Set([
  0xc0, 0xc1, 0xc2, 0xc3, 0xc5, 0xc6, 0xc7, 0xc9, 0xca, 0xcb, 0xcd, 0xce, 0xcf,
]);

function readAscii(bytes: Uint8Array, offset: number, length: number): string {
  return String.fromCharCode(...bytes.slice(offset, offset + length));
}

function readU16Le(bytes: Uint8Array, offset: number): number {
  return bytes[offset]! + (bytes[offset + 1]! << 8);
}

function readU16Be(bytes: Uint8Array, offset: number): number {
  return (bytes[offset]! << 8) + bytes[offset + 1]!;
}

function readU24Le(bytes: Uint8Array, offset: number): number {
  return bytes[offset]! + (bytes[offset + 1]! << 8) + (bytes[offset + 2]! << 16);
}

function readU32Be(bytes: Uint8Array, offset: number): number {
  return (
    ((bytes[offset]! << 24) >>> 0) +
    ((bytes[offset + 1]! << 16) | (bytes[offset + 2]! << 8) | bytes[offset + 3]!)
  );
}

function startsWith(bytes: Uint8Array, signature: readonly number[]): boolean {
  return signature.every((value, index) => bytes[index] === value);
}

function looksScriptableOrArchive(bytes: Uint8Array): boolean {
  const head = readAscii(bytes, 0, Math.min(bytes.length, 256)).trimStart().toLowerCase();
  return (
    head.startsWith('<svg') ||
    head.startsWith('<?xml') ||
    head.startsWith('<!doctype html') ||
    head.startsWith('<html') ||
    head.startsWith('{') ||
    head.startsWith('[') ||
    head.startsWith('%pdf') ||
    head.startsWith('pk')
  );
}

function parsePng(bytes: Uint8Array): StorefrontMediaUploadInspection | null {
  const pngSignature = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
  if (bytes.length < 24 || !startsWith(bytes, pngSignature) || readAscii(bytes, 12, 4) !== 'IHDR') {
    return null;
  }
  return { mimeType: 'image/png', width: readU32Be(bytes, 16), height: readU32Be(bytes, 20) };
}

function parseJpeg(bytes: Uint8Array): StorefrontMediaUploadInspection | null {
  if (bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8) return null;
  let offset = 2;
  while (offset + 3 < bytes.length) {
    if (bytes[offset] !== 0xff) {
      offset += 1;
      continue;
    }
    while (bytes[offset] === 0xff) offset += 1;
    const marker = bytes[offset]!;
    offset += 1;
    if (marker === 0xd9 || marker === 0xda) break;
    if (offset + 1 >= bytes.length) return null;
    const length = readU16Be(bytes, offset);
    if (length < 2 || offset + length > bytes.length) return null;
    if (JPEG_SOF_MARKERS.has(marker)) {
      if (length < 7) return null;
      return {
        mimeType: 'image/jpeg',
        height: readU16Be(bytes, offset + 3),
        width: readU16Be(bytes, offset + 5),
      };
    }
    offset += length;
  }
  return null;
}

function parseWebp(bytes: Uint8Array): StorefrontMediaUploadInspection | null {
  if (bytes.length < 16 || readAscii(bytes, 0, 4) !== 'RIFF' || readAscii(bytes, 8, 4) !== 'WEBP') {
    return null;
  }
  const chunk = readAscii(bytes, 12, 4);
  if (chunk === 'VP8X' && bytes.length >= 30) {
    return {
      mimeType: 'image/webp',
      width: readU24Le(bytes, 24) + 1,
      height: readU24Le(bytes, 27) + 1,
    };
  }
  if (chunk === 'VP8 ' && bytes.length >= 30 && bytes[23] === 0x9d && bytes[24] === 0x01) {
    return {
      mimeType: 'image/webp',
      width: readU16Le(bytes, 26) & 0x3fff,
      height: readU16Le(bytes, 28) & 0x3fff,
    };
  }
  if (chunk === 'VP8L' && bytes.length >= 25 && bytes[20] === 0x2f) {
    const b0 = bytes[21]!;
    const b1 = bytes[22]!;
    const b2 = bytes[23]!;
    const b3 = bytes[24]!;
    return {
      mimeType: 'image/webp',
      width: 1 + (((b1 & 0x3f) << 8) | b0),
      height: 1 + (((b3 & 0x0f) << 10) | (b2 << 2) | ((b1 & 0xc0) >> 6)),
    };
  }
  return null;
}

function dimensionsAreValid(width: number, height: number): boolean {
  return (
    width >= STOREFRONT_MEDIA_UPLOAD_MIN_DIMENSION_PX &&
    width <= STOREFRONT_MEDIA_UPLOAD_MAX_DIMENSION_PX &&
    height >= STOREFRONT_MEDIA_UPLOAD_MIN_DIMENSION_PX &&
    height <= STOREFRONT_MEDIA_UPLOAD_MAX_DIMENSION_PX
  );
}

export function inspectStorefrontMediaImageBytes(
  bytes: Uint8Array,
  declaredMime: string | null | undefined,
): StorefrontMediaUploadInspection | StorefrontMediaUploadValidationError {
  if (bytes.length === 0) return 'image_required';
  if (bytes.length > STOREFRONT_MEDIA_UPLOAD_MAX_BYTES) return 'image_too_large';
  if (looksScriptableOrArchive(bytes)) return 'image_invalid_type';
  if (
    !declaredMime ||
    !(STOREFRONT_PUBLIC_MEDIA_ALLOWED_MIME_TYPES as readonly string[]).includes(declaredMime)
  ) {
    return 'image_invalid_type';
  }

  const parsed = parsePng(bytes) ?? parseJpeg(bytes) ?? parseWebp(bytes);
  if (!parsed || parsed.mimeType !== declaredMime) return 'image_invalid_type';
  if (!dimensionsAreValid(parsed.width, parsed.height)) return 'image_dimensions_invalid';
  return parsed;
}

export async function validateStorefrontMediaFile(
  file: File | null,
): Promise<StorefrontMediaUploadInspection | StorefrontMediaUploadValidationError> {
  if (!file || file.size === 0) return 'image_required';
  if (file.size > STOREFRONT_MEDIA_UPLOAD_MAX_BYTES) return 'image_too_large';
  const bytes = new Uint8Array(await file.arrayBuffer());
  return inspectStorefrontMediaImageBytes(bytes, file.type);
}

export function mapStorefrontMediaAssetError(error: unknown): StorefrontMediaAssetErrorCode {
  if (typeof error === 'string') {
    return mapStorefrontMediaAssetErrorCode(error);
  }
  if (error && typeof error === 'object') {
    const maybe = error as {
      readonly code?: unknown;
      readonly error?: unknown;
      readonly message?: unknown;
      readonly name?: unknown;
    };
    const code = typeof maybe.code === 'string' ? maybe.code : null;
    const responseError = typeof maybe.error === 'string' ? maybe.error : null;
    if (code) return mapStorefrontMediaAssetErrorCode(code);
    if (responseError) return mapStorefrontMediaAssetErrorCode(responseError);
    const messageText = typeof maybe.message === 'string' ? maybe.message.trim() : '';
    const messageCode = messageText ? mapStorefrontMediaAssetErrorCode(messageText) : 'unknown';
    if (messageCode !== 'unknown') return messageCode;
    const message = `${maybe.name ?? ''} ${messageText}`;
    if (/quota|storage_limit|limit/i.test(message)) return 'storage_quota_exceeded';
    if (/permission|unauthorized|forbidden/i.test(message)) return 'permission_denied';
    if (/network|fetch|abort|load failed/i.test(message)) return 'network_failed';
  }
  return 'unknown';
}

export function mapStorefrontMediaAssetErrorCode(code: string): StorefrontMediaAssetErrorCode {
  if (
    code === 'image_required' ||
    code === 'image_too_large' ||
    code === 'image_invalid_type' ||
    code === 'image_dimensions_invalid' ||
    code === 'storage_quota_exceeded' ||
    code === 'media_in_use' ||
    code === 'permission_denied' ||
    code === 'network_failed' ||
    code === 'upload_failed' ||
    code === 'remove_failed' ||
    code === 'invalid_request'
  ) {
    return code;
  }
  if (code === '42501' || code === 'PGRST301') return 'permission_denied';
  if (code === 'P0601' || code === 'P0602') return 'storage_quota_exceeded';
  if (code === 'P0771') return 'media_in_use';
  if (code === '22023') return 'invalid_request';
  return 'unknown';
}

export function serializeStorefrontMediaAssetOptionForPublicReference(
  option: StorefrontMediaAssetOption,
): Pick<StorefrontMediaAssetOption, 'id' | 'role' | 'status' | 'isSelectable'> {
  return {
    id: option.id,
    role: option.role,
    status: option.status,
    isSelectable: option.isSelectable,
  };
}
